import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TPipelineSelectProps } from '@/console/features/pipelines/components/pipeline-select/pipeline-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadPipelinesForSelectQuery } from '@/shared/redux/api/jobs/jobs-api';
import {
  PipelineConfigurationForSelectFragment,
  ReadPipelinesForSelectQuery,
} from '@/shared/types/types.generated';

export const PipelineSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  pipelineFilter,
  getItemId,
  createOption,
  disableTags = true,
  enableTagsTooltip,
  filterSelectedTags,
  additionalOptions = [],
  onSelectChange,
  multiple = true,
}: TPipelineSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();

  const createDefaultOption = (
    pipelineConfiguration: PipelineConfigurationForSelectFragment,
  ) => {
    return {
      label: pipelineConfiguration.name,
      value: pipelineConfiguration.pipelineConfigurationId,
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;
  const transformData = (data?: ReadPipelinesForSelectQuery) => {
    if (!data) {
      return [];
    }

    const pipelines = data.readAllPipelineConfigurations.items;
    const validGateways = pipelineFilter
      ? pipelines.filter((pipeline) => pipelineFilter(pipeline))
      : pipelines;

    const options = validGateways.map((pipeline) => _createOption(pipeline));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<
      TFieldValues,
      ReadPipelinesForSelectQuery,
      TOptionShape
    >
      name={name}
      label={t('pipelines:select_pipeline', { count: multiple ? 2 : 1 })}
      placeholder={t('pipelines:select_pipeline', { count: multiple ? 2 : 1 })}
      disableTags={disableTags}
      enableTagsTooltip={enableTagsTooltip}
      filterSelectedTags={filterSelectedTags}
      multiple={multiple}
      required={true}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadPipelinesForSelectQuery,
        transformData,
      }}
      onSelectChange={onSelectChange}
    />
  );
};
